import * as React from 'react'
import { AlertTriangle, Loader2, Search, Sparkles } from 'lucide-react'

import { useSimilarDomains, type SimilarVariant } from '@/lib/queries'
import { ApiError } from '@/lib/api'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Checkbox, Label } from '@/components/ui/misc'
import { Tooltip } from '@/components/ui/tooltip'
import { useToast } from '@/components/ui/toast'
import { cn } from '@/lib/utils'

/**
 * 相似域名推荐。
 *
 * 每条建议都带着产生它的规则和具体改动（§18.8）。这不是为了好看：一个说不出
 * 「这是把你的后缀换成 .cn」的推荐器没法被审查，迟早会推出别人的商标而无法解释
 * 它是怎么来的。
 *
 * 这里不做任何可注册查询。生成变体是免费的，查可注册要花外部查询额度，
 * 所以"要不要查"是用户的决定，不是页面替他做的。
 */
export function SimilarPanel({ seed }: { seed?: string }) {
  const toast = useToast()
  const generate = useSimilarDomains()
  const [domain, setDomain] = React.useState(seed ?? '')
  const [kinds, setKinds] = React.useState<string[]>([])
  const [result, setResult] = React.useState<Awaited<ReturnType<typeof generate.mutateAsync>> | null>(null)

  async function run() {
    if (!domain.trim()) return
    try {
      setResult(await generate.mutateAsync({
        domain: domain.trim(),
        kinds: kinds.length ? kinds : undefined,
      }))
    } catch (err) {
      toast.error('生成失败', err instanceof ApiError ? err.message : undefined)
    }
  }

  const allKinds = result?.kinds ?? []

  return (
    <div className="space-y-3">
      <div className="flex items-end gap-2">
        <div className="flex-1 space-y-1">
          <Label className="text-xs">种子域名</Label>
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" aria-hidden />
            <Input
              className="h-9 pl-8" value={domain} placeholder="example.com"
              onChange={(e) => setDomain(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') void run() }}
            />
          </div>
        </div>
        <Button onClick={() => void run()} disabled={generate.isPending || !domain.trim()}>
          {generate.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />}
          <Sparkles className="h-3.5 w-3.5" aria-hidden /> 生成变体
        </Button>
      </div>

      {allKinds.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {allKinds.map((k) => (
            <label key={k.kind} className="inline-flex items-center gap-1.5 rounded-md border px-2 py-1 text-xs">
              <Checkbox
                checked={kinds.includes(k.kind)}
                onCheckedChange={(v) =>
                  setKinds((s) => (v === true ? [...s, k.kind] : s.filter((x) => x !== k.kind)))}
              />
              {k.label}
            </label>
          ))}
          <span className="self-center text-[0.7rem] text-muted-foreground">
            不勾＝全部规则
          </span>
        </div>
      )}

      {result && (
        <>
          <p className="flex items-start gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 p-2 text-xs">
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-600" aria-hidden />
            <span>{result.disclaimer}</span>
          </p>
          <VariantTable variants={result.variants} />
        </>
      )}
    </div>
  )
}

function VariantTable({ variants }: { variants: SimilarVariant[] }) {
  if (variants.length === 0) {
    return <p className="p-4 text-center text-xs text-muted-foreground">没有生成任何变体。</p>
  }
  return (
    <div className="scroll-x rounded-lg border">
      <table className="w-full min-w-[640px] text-sm">
        <caption className="sr-only">相似域名变体，共 {variants.length} 条</caption>
        <thead className="border-b bg-muted/40">
          <tr>
            {['域名', '规则', '怎么来的', '平台是否收录'].map((h) => (
              <th key={h} scope="col" className="whitespace-nowrap px-3 py-2 text-left text-xs font-medium text-muted-foreground">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {variants.map((v) => (
            <tr key={v.domain_ascii} className="border-b last:border-0 hover:bg-muted/40">
              <td className="px-3 py-2 font-mono text-xs">{v.domain_ascii}</td>
              <td className="px-3 py-2">
                <Badge variant="secondary">{v.kind}</Badge>
              </td>
              <td className="px-3 py-2 text-xs text-muted-foreground">{v.explanation}</td>
              <td className="px-3 py-2 text-xs">
                {v.known ? (
                  <span className={cn(v.status === 'available' && 'font-medium text-emerald-600')}>
                    {v.status}
                  </span>
                ) : (
                  <Tooltip content="平台从没见过这个域名。这不代表它可注册——要知道能不能注册，得走查询接口，那会消耗外部查询额度。">
                    <Badge variant="muted" className="cursor-help">未收录</Badge>
                  </Tooltip>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
